import React, { useCallback, useEffect, useRef, useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import {
  disableWheelInteraction,
  performStep,
  prepareNumberValue,
} from '../../../core/components/form-item/form-item-utils';
import HTMLRender from '../../../core/components/html-render/html-render';
import Tooltip from '../../../core/components/tooltip/tooltip';
import { useLocale } from '../../../core/hooks/use-locale';
import { Icon, iconOnly } from '../../../core/utils/icons';

import { InputProps } from './form-item-types';

const Input = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  isRequired,
  isRequiredMarked,
  isDisabled,
  help,
  isNarrow,
  isUpdatedMessage,
  isHighlight,
  placeholder,
  type,
  min,
  max,
  step,
  pattern,
  adornment,
  tooltip,
  autocomplete,
}: InputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useRef(uuidv4());
  const { Message } = useLocale();

  const [currentType, setCurrentType] = useState(type);
  const [requiredMarked, setRequiredMarked] = useState(true);
  const [updatedMessage, setUpdatedMessage] = useState(false);

  const handleChange = useCallback(
    (value: React.ReactText) => {
      type === 'number'
        ? onChange(prepareNumberValue(value, ''))
        : onChange(value);
    },
    [onChange, type]
  );

  useEffect(() => {
    if (!value) {
      if (type === 'url') {
        handleChange('https://');
      }
    }

    if (isRequiredMarked) {
      setRequiredMarked(isRequiredMarked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequiredMarked]);

  useEffect(() => {
    if (isUpdatedMessage) {
      setUpdatedMessage(isUpdatedMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatedMessage]);


  if (type === 'number') console.log(value);

  return (
    <div
      className={classNames('input', {
        'input--has-arrows': type === 'number',
        'input--has-icon': type === 'password',
        'form-item--error': error,
        'form-item--help': help,
        'form-item--disabled': isDisabled,
        'form-item--narrow': isNarrow,
        'form-item--highlighted': isHighlight,
      })}
    >
      {(label || help) && (
        <label htmlFor={id.current} className="form-item__label">
          {label}
          {requiredMarked && isRequired && typeof label === 'string' && (
            <span className="form-item__required">*</span>
          )}
          {requiredMarked && !isRequired && typeof label === 'string' && (
            <span> {Message.OPTIONAL}</span>
          )}
          {updatedMessage && (
            <span className="screen-reader-only">{Message.UPDATE_MESSAGE}</span>
          )}
          {!!tooltip && (
            <div className="form-item__tooltip">
              <HTMLRender html={tooltip} />
            </div>
          )}
          {error ? (
            <div className="form-item__error">{error}</div>
          ) : help ? (
            <div className="form-item__help">{help}</div>
          ) : null}
        </label>
      )}
      <div className="input__outer-wrapper">
        <div className="input__wrapper">
          <input
            ref={inputRef}
            id={id.current}
            name={name}
            value={value}
            onChange={({ target: { value } }) => handleChange(value)}
            onBlur={onBlur}
            placeholder={placeholder}
            required={isRequired}
            aria-required={isRequired}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            type={currentType}
            className="input__control"
            min={min}
            max={max}
            step={step}
            autoComplete={autocomplete}
            pattern={type === 'url' ? pattern ?? 'https?://.*' : pattern}
            {...(type === 'number' && { onWheel: disableWheelInteraction })}
          />
          {type === 'number' && (
            <div className="input__arrows">
              <Tooltip title={Message.ADD} placement="top">
                <button
                  type="button"
                  onClick={performStep('+', inputRef.current, handleChange)}
                  className={classNames(
                    'input__arrow',
                    iconOnly(Icon.ANGLE_UP, null)
                  )}
                  disabled={isDisabled}
                ></button>
              </Tooltip>
              <hr />
              <Tooltip title={Message.REMOVE}>
                <button
                  type="button"
                  onClick={performStep('-', inputRef.current, handleChange)}
                  className={classNames(
                    'input__arrow',
                    iconOnly(Icon.ANGLE_DOWN, null)
                  )}
                  disabled={isDisabled}
                ></button>
              </Tooltip>
            </div>
          )}
          {type === 'password' && (
            <div className="input__icon">
              <Tooltip
                title={
                  currentType === 'password'
                    ? Message.SHOW_PASSWORD
                    : Message.HIDE_PASSWORD
                }
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    setCurrentType((prevType) =>
                      prevType === 'password' ? 'text' : 'password'
                    )
                  }
                  disabled={isDisabled}
                >
                  {currentType === 'password' ? (
                    <VisibilityIcon />
                  ) : (
                    <VisibilityOffIcon />
                  )}
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
        {adornment && <div className="input__adornment">{adornment}</div>}
      </div>
    </div>
  );
};

export default Input;
